import { ArrowBack, TocOutlined } from "@mui/icons-material";
import {
  Chip,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import moment from "moment";

import DocumentGeneralJournalAdjustingActions from "../DocumentGeneralJournalAdjustingActions";
import NoRecordsFound from "../../../../components/NoRecordsFound";
import { formatMonthToString } from "../../accrual-reversal/_utils/format-month";
import TablePreloader from "../TablePreloader";
import GJChip from "../../../../components/GJChip";
import { useQueryParams } from "../../../../hooks/useQueryParams";

const Month = ({ rootPath }) => {
  const { year, month } = useParams();
  const { searchParams } = useQueryParams();

  const navigate = useNavigate();
  const {
    data,
    handleViewGJAdjustingEntries,
    handleManageGJAdjustingEntries,
    handleDeleteGJAdjustingEntries,
    handlePrint,
    isError,
    isFetching,
  } = useOutletContext();

  const onNavigateBack = () => {
    const newParams = new URLSearchParams();
    newParams.set("is_posted", "0");
    newParams.set("status", "pending");
    navigate({
      pathname: `/general-journal/${rootPath}/${year}`,
      search: newParams.toString(),
    });
  };

  const status = searchParams.get("status"); // "pending"
  const is_posted = searchParams.get("is_posted");

  return (
    <>
      <Stack>
        <Divider sx={{ marginTop: 2 }} />
        <Stack direction="row" gap={1} height="6vh" alignItems="center" padding={2}>
          <Tooltip title="Back">
            <IconButton onClick={onNavigateBack} color="inherit">
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Stack direction="row" gap={1}>
            <TocOutlined sx={{ opacity: 0.9 }} />
            <Typography fontWeight={500} fontSize={13} sx={{ opacity: 0.9 }}>
              General Journal Month: <GJChip title={formatMonthToString(month)} />
            </Typography>
          </Stack>
        </Stack>
        <Divider />
      </Stack>
      <TableContainer className="FstoTableContainer-root">
        <Table className="FstoTable-root" size="small">
          <TableHead className="FstoTableHead-root">
            <TableRow className="FstoTableRow-root">
              <TableCell className="FstoTableCell-root FstoTableCell-head FstoTabCell-color">
                <TableSortLabel active={false}>GJ NUMBER</TableSortLabel>
              </TableCell>
              <TableCell className="FstoTableCell-root FstoTableCell-head FstoTabCell-color">
                <TableSortLabel active={false}>JOURNAL NAME</TableSortLabel>
              </TableCell>
              <TableCell className="FstoTableCell-root FstoTableCell-head FstoTabCell-color">
                <TableSortLabel active={false}>DESCRIPTION</TableSortLabel>
              </TableCell>
              <TableCell className="FstoTableCell-root FstoTableCell-head FstoTabCell-color">
                <TableSortLabel active={false}>DIVISION</TableSortLabel>
              </TableCell>
              {!isFetching && (
                <TableCell className="FstoTableCell-root FstoTableCell-head FstoTabCell-color">
                  <TableSortLabel active={false}>STATUS</TableSortLabel>
                </TableCell>
              )}
              {!isFetching && Boolean(is_posted) && (
                <TableCell
                  className="FstoTableCell-root FstoTableCell-head FstoTabCell-color"
                  sx={{ width: "10%" }}
                >
                  <TableSortLabel active={false}>ADJUSTMENT MONTH</TableSortLabel>
                </TableCell>
              )}
              <TableCell
                className="FstoTableCell-root FstoTableCell-head FstoTabCell-color"
                align="right"
              >
                ACTIONS
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody className="FstoTableBody-root">
            {!isFetching &&
              data?.data?.map((item, index) => (
                <TableRow className="FstoTableRow-root" key={index} hover>
                  <TableCell className="FstoTableCell-root FstoTableCell-body">
                    <Typography className="FstoTypography-general_journal">
                      {item.gj_number}
                    </Typography>
                  </TableCell>
                  <TableCell className="FstoTableCell-root FstoTableCell-body">
                    <Typography className="FstoTypography-general_journal">
                      {item.journal_name?.toUpperCase()}
                    </Typography>
                  </TableCell>
                  <TableCell className="FstoTableCell-root FstoTableCell-body">
                    <Typography className="FstoTypography-general_journal">
                      {item.journal_description?.toUpperCase()}
                    </Typography>
                  </TableCell>
                  <TableCell className="FstoTableCell-root FstoTableCell-body">
                    <Typography className="FstoTypography-general_journal">
                      {item?.division?.name}
                    </Typography>
                  </TableCell>
                  <TableCell className="FstoTableCell-root FstoTableCell-body">
                    <Chip
                      label={status}
                      color="warning"
                      height="inherit"
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>
                  {Boolean(is_posted) && (
                    <TableCell className="FstoTableCell-root FstoTableCell-body">
                      <Typography className="FstoTypography-general_journal">
                        {item?.adjustment_month &&
                          moment(item?.adjustment_month).format("MMM YYYY")}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell className="FstoTableCell-root FstoTableCell-body" align="right">
                    <DocumentGeneralJournalAdjustingActions
                      is_posted={is_posted}
                      data={item}
                      onView={handleViewGJAdjustingEntries}
                      onManage={handleManageGJAdjustingEntries}
                      onDelete={handleDeleteGJAdjustingEntries}
                      onPrint={handlePrint}
                    />
                  </TableCell>
                </TableRow>
              ))}
            {Boolean(isFetching) && <TablePreloader row={3} />}
          </TableBody>
        </Table>
        {(isError || Boolean(!data?.data?.length)) && Boolean(!isFetching) && <NoRecordsFound />}
      </TableContainer>
    </>
  );
};

export default Month;
